// Derives the aggregate market value of a user's collection.
//
// Cards are only loaded into app state one set at a time (via loadSetCards),
// so a collection-wide dollar total needs its own lazy fetch: figure out
// which sets the user actually owns cards from (derived from collection key
// prefixes), fetch each of those sets' cards once per session through the
// existing /api/cards/:setId endpoint (the server already caches TCG API
// responses for 24h), and cache the resulting price map in memory so
// repeated calls don't refetch.

import PokemonTCGService from '../services/PokemonTCGService';

const CONCURRENCY = 3;

// cardId -> { regular, holo, reverse } — best-available market price per print bucket
const priceCache = new Map();
// cardId -> rarity string, filled from the same fetches — gives hit-rate
// calculations a collection-wide rarity lookup with zero extra network.
const rarityCache = new Map();
const fetchedSetIds = new Set();
const inFlightFetches = new Map(); // setId -> Promise, dedupes concurrent callers (e.g. StrictMode double-invoke)
let fetchQueue = Promise.resolve();

const extractPrices = (card) => {
  const prices = card.tcgplayer?.prices || {};
  const regular =
    prices.normal?.market ?? prices.unlimited?.market ?? prices['1stEdition']?.market ?? null;
  const holo =
    prices.holofoil?.market ?? prices.unlimitedHolofoil?.market ?? prices['1stEditionHolofoil']?.market ?? null;
  const reverse = prices.reverseHolofoil?.market ?? null;
  return { regular, holo, reverse };
};

// Owned setIds derived from collection key prefixes (e.g. "sv3pt5-25" -> "sv3pt5").
// Uses lastIndexOf so multi-hyphen set ids (e.g. "sv4pt5") resolve correctly.
export const getOwnedSetIds = (collection) => {
  const setIds = new Set();
  Object.entries(collection).forEach(([cardId, variants]) => {
    const hasQty = Object.values(variants).some((qty) => qty > 0);
    if (!hasQty) return;
    const lastDash = cardId.lastIndexOf('-');
    if (lastDash <= 0) return;
    setIds.add(cardId.substring(0, lastDash));
  });
  return Array.from(setIds);
};

export const hasAllSetsFetched = (setIds) => setIds.every((id) => fetchedSetIds.has(id));

const fetchSetPrices = (setId) => {
  if (fetchedSetIds.has(setId)) return Promise.resolve();
  if (inFlightFetches.has(setId)) return inFlightFetches.get(setId);

  const promise = PokemonTCGService.fetchSetCards(setId)
    .then(({ data }) => {
      data.forEach((card) => {
        priceCache.set(card.id, extractPrices(card));
        if (card.rarity) rarityCache.set(card.id, card.rarity);
      });
      fetchedSetIds.add(setId);
    })
    .catch((err) => {
      console.error(`Failed to load prices for set ${setId}:`, err);
    })
    .finally(() => {
      inFlightFetches.delete(setId);
    });

  inFlightFetches.set(setId, promise);
  return promise;
};

// Queues fetches for any owned sets not already cached, at a capped
// concurrency, and resolves once every requested set has been attempted
// (success or not — a failed set just contributes 0 to the total).
export const ensurePricesLoaded = (setIds) => {
  const pending = setIds.filter((id) => !fetchedSetIds.has(id));
  if (pending.length === 0) return Promise.resolve();

  fetchQueue = fetchQueue.then(async () => {
    for (let i = 0; i < pending.length; i += CONCURRENCY) {
      const batch = pending.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(fetchSetPrices));
    }
  });

  return fetchQueue;
};

export const getCardRarity = (cardId) => rarityCache.get(cardId) || null;

export const variantToPrice = (cardId, variantName) => {
  const prices = priceCache.get(cardId);
  if (!prices) return 0;
  // Pattern reverses (Poke Ball, Master Ball, ...) are sold as their own
  // TCGPlayer SKUs, but our cached data only carries the base three — the
  // plain reverse price is the closest available proxy for them.
  if (variantName === 'Regular') return prices.regular ?? prices.holo ?? 0;
  if (variantName === 'Holo') return prices.holo ?? prices.regular ?? 0;
  return prices.reverse ?? prices.holo ?? prices.regular ?? 0;
};

export const calculatePortfolioValue = (collection) => {
  let total = 0;
  Object.entries(collection).forEach(([cardId, variants]) => {
    Object.entries(variants).forEach(([variantName, qty]) => {
      if (qty > 0) {
        total += variantToPrice(cardId, variantName) * qty;
      }
    });
  });
  return total;
};

export const formatCurrency = (value) => `$${Math.round(value).toLocaleString()}`;

export const formatCurrencyCompact = (value) => {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${Math.round(value)}`;
};
